import {BaseStage} from "./BaseStage";
import {initFlatPickerInput} from "../../../helpers/inputs/flatPicker/flatPicker";
import createElement from "../../../helpers/createElement";

class DateSelectStage extends BaseStage {
    /** @var {Object<string,PublicEventResource.PublicEventResource[]>} */
    eventsByDate;
    flatpickrHtmlInitializedIntervalId;
    /** @var {HTMLElement} */
    templatesContainer;

    constructor(globalState) {
        super(globalState);

        // Init dom container
        this.domContainer = document.querySelector(`.stage.${this.getStageKey()}`)

        // Retrieve template container
        this.templatesContainer = document.querySelector('.templates .date-select-stage-template');

        // Clone template
        const stageWrapper = this.templatesContainer.querySelector('.stage-wrapper').cloneNode(true);

        // Prepare pending event dates to place in calendar
        //// Group events by their date
        //// Each key is a HTML date and the contents an array of events for that day
        /** @var {Object<string,PublicEventResource.PublicEventResource[]>} */
        this.eventsByDate = {};

        this.globalState.parentEvent.pendingDates.forEach(event => {
            if (this.eventsByDate[event.htmlDate] === undefined) {
                this.eventsByDate[event.htmlDate] = [];
            }

            this.eventsByDate[event.htmlDate].push(event);
        })

        //// Get enabled dates
        const enabledDatesArray = Object.keys(this.eventsByDate);

        // Init flatpickr instance
        const calendarElement = stageWrapper.querySelector('.dummy-input');

        this.flatPickrInstance = initFlatPickerInput(calendarElement, {
            inline: true,
            enable: enabledDatesArray,
            // Because events come presorted, first and last
            // dates in the array are also the first and last shows
            minDate: enabledDatesArray[0],
            maxDate: enabledDatesArray[enabledDatesArray.length - 1],

            // Update HTML on change
            onChange: () => {
                this.initializeFlatpickrDateHtml();
                this.handleDateChange();
            },
            onMonthChange: () => this.initializeFlatpickrDateHtml(),
        })

        // Update HTML on a interval, as a fallback
        this.flatpickrHtmlInitializedIntervalId = setInterval(() => {
            this.initializeFlatpickrDateHtml();
        }, 16)

        // Store date info wrapper
        this.dateInfoWrapper = stageWrapper.querySelector('.show-info-container-wrapper');


        // Append stage to DOM
        this.domContainer.appendChild(stageWrapper);

        // Run base class initializations
        this.initSharedComponents();
    }

    /**
     * @return {PublicTicketsFormDefs.Stage}
     */
    getStageKey() {
        // noinspection JSValidateTypes
        return 'date-select';
    }

    clearDom() {
        // Clear stage HTML
        this.domContainer.innerHTML = '';

        // Stop interval
        clearInterval(this.flatpickrHtmlInitializedIntervalId);
    }

    /**
     * Flatpickr does not let us easily change the days HTML content
     * So this takes care of doing it
     */
    initializeFlatpickrDateHtml() {
        // Check for all non initialized elements
        this.domContainer.querySelectorAll(".flatpickr-day:not(.initialized)")
            .forEach(calendarDayContainer => {
                const defaultContent = calendarDayContainer.innerHTML;

                let customContentString = '';

                // Create Date instance from aria label
                const dateInstance = new Date(calendarDayContainer.ariaLabel);

                const dayName = dateInstance.toLocaleDateString('en-US', {
                    weekday: 'long',
                }).toUpperCase();

                // Retrieve related event date
                const htmlDate = dateInstance.toISOString().split('T')[0];

                const relatedEvents = this.eventsByDate[htmlDate];

                let eventTimesString = '';

                if (relatedEvents) {
                    eventTimesString = relatedEvents.map(event => {
                        return `<div class="event-time">${event.timeOnly}</div>`;
                    }).join('\n');
                }

                // Retrieve custom content if exists
                const dateCustomContentElement = document.querySelector(`.pre-show-entertainment-calendar *[data-date="${calendarDayContainer.ariaLabel}"]`)

                // Enabled only for christmas event with id 126
                if (dateCustomContentElement && this.globalState.parentEvent.id === '126') {
                    customContentString = dateCustomContentElement.innerHTML;
                }

                // Update HTML
                calendarDayContainer.innerHTML = `
                    <div class="large-screen d-none d-md-block">
                         <div class="day-header">
                            <div class="day-number">
                                ${defaultContent}
                            </div>
                            <div
                                class="day-name small text-muted pl-2">
                                ${dayName}
                            </div>
                        </div>
                        <div class="date-details px-3">
                            <div class="times-container">
                                ${eventTimesString}
                            </div>
                            <div class="custom-content-container">
                                ${customContentString}
                            </div>
                        </div>
                    </div>
                    <div class="small-screen d-block d-md-none">
                        <div class="day-number">
                            ${defaultContent}
                        </div>
                    </div>
                `;

                calendarDayContainer.classList.add('initialized')
                // Also add a data- attribute with the date in HTML format
                calendarDayContainer.dataset.date = htmlDate;
            })
    }

    handleDateChange() {
        // Retrieve related date shows
        const date = this.flatPickrInstance.input.value;
        const shows = this.eventsByDate[date];

        if (shows.length === 1) {
            // Default to first available date
            this.globalState.setEventDate(shows[0]);
        } else {
            // Clear event date
            this.globalState.setEventDate(undefined);
        }

        // Disable stage advance
        this.disableNextStageButton();

        // Display event data
        this.renderDateInfoContainer(shows);

        // Scroll to recently added container, after a bit
        setTimeout(() => {
            window.scrollTo({
                behavior: 'smooth',
                top: 1000000,
            })
        }, 16)
    }

    /**
     * @param {PublicEventResource.PublicEventResource[]} dayShows
     */
    renderDateInfoContainer(dayShows) {
        const state = this.globalState;
        const parentEvent = state.parentEvent;
        const parentEventPrice = parentEvent.priceData;

        // Clear existing HTML
        this.dateInfoWrapper.innerHTML = '';

        // Clone new container
        const container = this.templatesContainer.querySelector('.show-info-container').cloneNode(true);

        // Set event name & description
        container.querySelector('.show-name').innerHTML = parentEvent.title;
        container.querySelector('.show-description').innerHTML = parentEvent.description;


        // Setup general and reserved price data and availability
        const generalAdmissionCard = container.querySelector('.ticket-type-card.general-admission')
        const reservedCard = container.querySelector('.ticket-type-card.reserved')

        generalAdmissionCard.querySelector('.price-range').innerHTML = '';

        reservedCard.querySelector('.price-range').innerHTML = '';

        // Hide tickets if section is not available
        if (parentEventPrice.generalAdmission.isAvailable === false) {
            generalAdmissionCard.classList.add('d-none')
        }
        if (parentEventPrice.reservedSection.isAvailable === false) {
            reservedCard.classList.add('d-none')
        }

        // Hide if not available

        // Set selected date
        let dateAndTimeContainer = container.querySelector('.date-and-time-container');

        // Pick the first day show to display in the section header
        const exampleDayShow = dayShows[0];

        if (dayShows.length > 1) {
            dateAndTimeContainer.innerHTML =
                `<div>${exampleDayShow.longDateOnly} <br>
                    <div class="time-select-container">
                        <select class="form-control performance-time-select mt-1" name="event_date_time" autocomplete="off">
                            <option value="" disabled selected>Select Performance</option>
                        </select>
                    </div>
                </div>`;

            /** @var {HTMLSelectElement} */
            const timeSelect = dateAndTimeContainer.querySelector('select');

            // Create options for each time, with the day's date index as the value
            dayShows.forEach((date, dateIndex) => {
                timeSelect.appendChild(createElement(`<option data-date="${date.timeOnly}" value="${dateIndex}">${date.timeOnly}</option>`));
            })

            // Update global state on date change
            timeSelect.addEventListener('change', () => {
                if (timeSelect.value) {
                    // Set date
                    const selectedSeatType = this.globalState.selectedSeatType;

                    this.globalState.setEventDate(dayShows[timeSelect.value]);

                    // Preserve previous selection if it exists
                    if (selectedSeatType) {
                        this.globalState.setSeatType(selectedSeatType);
                    }

                    // TODO: We need to re render this section, as each date
                    //  could potentially have different prices, ticket types and seat map...

                    // Clear selected seat type
                    // [generalAdmissionCard, reservedCard].forEach(ticketTypeCard => {
                    //     if (ticketTypeCard) {
                    //         const ticketInput = ticketTypeCard.querySelector(`input`);
                    //         // Mark as unchecked
                    //         ticketInput.checked = false;
                    //         // And clear class
                    //         ticketTypeCard.classList.remove('selected');
                    //
                    //     }
                    // })

                    // Check if we can continue
                    this.updateNextButtonStatus();
                }
            });
        } else {
            dateAndTimeContainer.innerHTML =
                `<div>${state.selectedDate.longDateOnly} <br> ${state.selectedDate.timeOnly}</div>`;
        }

        // Set custom content
        // Retrieve custom content if exists

        // Setup ticket selection
        [generalAdmissionCard, reservedCard].forEach(ticketTypeCard => {
            const ticketInput = ticketTypeCard.querySelector(`input`);

            ticketInput.addEventListener('change', () => {
                this.handleSeatTypeSelect(ticketTypeCard);
            })

            // Trigger show selection
            ticketTypeCard.addEventListener('click', () => {
                ticketInput.checked = true;
                ticketInput.dispatchEvent(new InputEvent('change'));
            })
        })

        // Update seat map image
        const seatImageElement = container.querySelector('.seat-map-image');
        const mapContainer = seatImageElement.closest('.map-container');

        // Check if we have a seat map image
        if (state.parentEvent.seatMapImageRoute) {
            // Display seat map
            mapContainer.classList.remove('d-none');
            seatImageElement.src = state.parentEvent.seatMapImageRoute;
        } else {
            // Hide seat map
            mapContainer.classList.add('d-none');
            seatImageElement.src = '';
        }

        // Update banner image
        const homeImageElement = container.querySelector('.home-map-image');
        const homeContainer = homeImageElement.closest('.map-container');

        // Check if we have a seat map image
        // TEMP: force hide home image
        homeContainer.classList.add('d-none');
        homeImageElement.src = '';

        // if (state.parentEvent.homeImageRoute) {
        //     // Display home image
        //     homeContainer.classList.remove('d-none');
        //     homeImageElement.src = state.parentEvent.homeImageRoute;
        // } else {
        //     // Hide home image
        //     homeContainer.classList.add('d-none');
        //     homeImageElement.src = '';
        // }

        // Append to DOM
        this.dateInfoWrapper.appendChild(container);
    }

    /**
     * @param {HTMLElement} ticketTypeContainer
     */
    handleSeatTypeSelect(ticketTypeContainer) {
        /** @var {HTMLInputElement} */
        const ticketTypeInput = ticketTypeContainer.querySelector(`input`);

        // Clear selection from other tickets
        this.domContainer.querySelectorAll('.ticket-type-card').forEach(ticketCard => {
            ticketCard.classList.remove('selected');
            ticketCard.querySelector('input').checked = false;
        })

        // Mark show as selected
        ticketTypeInput.checked = true;
        // And add selected class
        ticketTypeContainer.classList.add('selected');

        // Save global state
        // noinspection JSCheckFunctionSignatures
        this.globalState.setSeatType(ticketTypeInput.value);

        this.updateNextButtonStatus();
    }

    updateNextButtonStatus() {
        if (
            this.globalState.parentEvent &&
            this.globalState.selectedSeatType &&
            this.globalState.selectedDate
        ) {
            this.enableNextStageButton();
        } else {
            this.disableNextStageButton();
        }
    }
}

export {DateSelectStage};
